import { marketPositionNarrative } from "../clientPortal/propertyDnaExplain";
import { resolveAssessmentComparableCohort } from "../clientPortal/assessmentComparableCohort";
import { parseAssessedNumber } from "./calgaryAssessmentOpenData";
import { extractCurrentAssessmentSnapshot } from "./propertyAssessmentHistory";

function safeStr(value) {
  return String(value ?? "").trim();
}

function formatLotSize(sf) {
  const n = Number(sf);
  if (!Number.isFinite(n) || n <= 0) return null;
  return `${Math.round(n).toLocaleString()} sf`;
}

/**
 * @param {object|null|undefined} profile
 */
export function buildPropertyProfileSummary(profile) {
  const snap = profile?.myPropertyParcelSnapshot;
  const hasParcel = Boolean(
    snap &&
      typeof snap === "object" &&
      safeStr(snap.comm_code || profile?.myPropertyParcelRoll)
  );
  const snapshot = extractCurrentAssessmentSnapshot(profile);
  const cohort = hasParcel ? resolveAssessmentComparableCohort(snap) : null;

  const propertyType = safeStr(snap?.property_type) || null;
  const subUse = safeStr(snap?.sub_property_use) || null;
  const rollTypeLabel = cohort?.label || (subUse ? `Roll code ${subUse}` : propertyType) || null;

  return {
    hasParcel,
    commCode: safeStr(snap?.comm_code) || null,
    community:
      safeStr(profile?.myPropertyParcelCommunity) ||
      safeStr(snap?.comm_name) ||
      null,
    propertyType,
    rollTypeLabel,
    subPropertyUse: subUse || null,
    yearBuilt: snap?.year_of_construction != null ? String(snap.year_of_construction) : null,
    lotSizeLabel: formatLotSize(snap?.land_size_sf),
    landUseDesignation: safeStr(snap?.land_use_designation) || null,
    assessmentClass:
      safeStr(snap?.assessment_class_description) ||
      safeStr(snap?.assessment_class) ||
      null,
    currentAssessedValue:
      snapshot?.assessedValue ??
      parseAssessedNumber(snap?.assessed_value ?? snap?.re_assessed_value),
    parcelSnapshot: snap || null,
  };
}

/**
 * @param {number|null|undefined} assessed
 * @param {number|null|undefined} peerAvg
 * @returns {{ band: 'below'|'near'|'above'|'unknown', label: string }}
 */
export function classifyAssessmentVsPeerAverage(assessed, peerAvg) {
  const a = Number(assessed);
  const avg = Number(peerAvg);
  if (!Number.isFinite(a) || !Number.isFinite(avg) || avg <= 0) {
    return { band: "unknown", label: "—" };
  }
  const ratio = a / avg;
  if (ratio < 0.92) return { band: "below", label: "Below typical" };
  if (ratio > 1.08) return { band: "above", label: "Above typical" };
  return { band: "near", label: "Near typical" };
}

/**
 * @param {'below'|'near'|'above'|'unknown'} band
 */
export const CMA_MARKET_CONTEXT_ASSESSMENT_ROLL_TITLE = "Assessment roll context";

export const CMA_MARKET_CONTEXT_ASSESSMENT_ROLL_INTRO =
  "This section uses municipal assessment/open-data context only. It does not replace MLS sold comparables.";

export const CMA_MARKET_CONTEXT_MLS_HELPER =
  "MLS comparable value is built on the MLS comps step. This step is background context only.";

export const CMA_MARKET_CONTEXT_PEER_UNAVAILABLE =
  "Community peer averages are not available for this parcel yet.";

export const CMA_BASELINE_MARKET_COMBINED_INTRO =
  "This step provides assessment/open-data background only. MLS comparable value is built on the MLS comps step.";

export function buildAssessmentComparisonHeadline(band) {
  if (band === "below") {
    return "This parcel is assessed below the community peer average on the municipal roll.";
  }
  if (band === "above") {
    return "This parcel is assessed above the community peer average on the municipal roll.";
  }
  if (band === "near") {
    return "This parcel is assessed near the community peer average on the municipal roll.";
  }
  return CMA_MARKET_CONTEXT_PEER_UNAVAILABLE;
}

export const MARKET_CONTEXT_DISCLAIMERS = [
  "Assessment is not market value.",
  "Market value depends on condition, renovations, exposure, timing, and active sales.",
];

/**
 * @param {object} params
 * @param {ReturnType<typeof buildPropertyProfileSummary>} params.summary
 * @param {object|null} [params.peerCommunityRow] — cohort roll stats
 * @param {object|null} [params.communityBroadRow] — all RE in community
 * @param {{ id?: string, label?: string }|null} [params.cohort]
 */
export function buildMarketContextViewModel({
  summary,
  peerCommunityRow = null,
  communityBroadRow = null,
  cohort = null,
}) {
  if (!summary?.hasParcel) {
    return { ready: false, summary, comparison: null, interpretation: null };
  }

  const assessed = summary.currentAssessedValue;
  const peerAvg = parseAssessedNumber(peerCommunityRow?.avg_assessed);
  const communityAvg = parseAssessedNumber(communityBroadRow?.avg_assessed);
  const position = classifyAssessmentVsPeerAverage(assessed, peerAvg);
  const broadMixed = cohort?.id === "re_broad";
  const narrative = marketPositionNarrative(assessed, peerCommunityRow, {
    peerGroupLabel: cohort?.label,
    broadMixedRE: broadMixed,
  });

  return {
    ready: true,
    summary,
    comparison: {
      communityName: summary.community || peerCommunityRow?.comm_name || "Your community",
      communityAvg,
      peerGroupLabel: cohort?.label || "Similar properties (roll peer group)",
      peerAvg,
      peerMin: parseAssessedNumber(peerCommunityRow?.min_assessed),
      peerMax: parseAssessedNumber(peerCommunityRow?.max_assessed),
      peerParcelCount: parseAssessedNumber(peerCommunityRow?.parcel_count),
      position,
      broadMixed,
    },
    interpretation: {
      headline: buildAssessmentComparisonHeadline(position.band),
      detail: narrative,
      disclaimers: MARKET_CONTEXT_DISCLAIMERS,
    },
  };
}
